import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Mail, Phone, MapPin, Globe, Send, CheckCircle2, AlertCircle } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    setError("");

    const serviceID = "service_2afp364";
    const templateID = "template_63o966n";
    const publicKey = "-lAh0SHoDA_i7Eys5";

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      from_phone: formData.phone,
      message: formData.message,
    };

    emailjs
      .send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        setIsSent(true);
        setFormData({ name: "", email: "", phone: "", message: "" });
        setTimeout(() => setIsSent(false), 5000);
      })
      .catch((err) => {
        setError("Failed to send message. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>Contact Us | Sooners Land LLC</title>
        <meta name="description" content="Get in touch with Sooners Land LLC for engineering consultant services and project management." />
      </Helmet>

      {/* Header */}
      <section className="bg-primary pt-32 pb-16 px-6 md:px-12 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/4 h-full bg-accent/5 -skew-x-12 translate-x-1/2" />
        <div className="container mx-auto relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Let's Build <span className="text-accent italic">Together</span>
          </motion.h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Ready to start your next infrastructure project? Our global team is here to help you succeed.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-6 md:px-12 py-16 -mt-10">
        <div className="grid lg:grid-cols-5 gap-12 max-w-7xl mx-auto">

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 space-y-8"
          >
            <div className="glass-card p-10 rounded-[2.5rem] shadow-xl border border-slate-100 bg-white">
              <h2 className="text-2xl font-bold mb-8 text-primary flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-soft rounded-lg flex items-center justify-center">
                  <Globe size={20} className="text-accent" />
                </div>
                Global Presence
              </h2>

              <div className="space-y-10">
                <div className="flex gap-4">
                  <Phone size={24} className="text-accent shrink-0 mt-1" />
                  <div className="space-y-2">
                    <h3 className="font-bold text-lg text-primary">Direct Lines</h3>
                    <p className="text-text-muted">🇺🇸 USA: <span className="text-primary font-semibold">+1 281 626 3263</span></p>
                    <p className="text-text-muted">🇹🇷 Turkey: <span className="text-primary font-semibold">+90 531 273 7922</span></p>
                    <p className="text-text-muted">🇸🇦 KSA: <span className="text-primary font-semibold">+966 567 5599 63</span></p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Mail size={24} className="text-accent shrink-0 mt-1" />
                  <div className="space-y-2">
                    <h3 className="font-bold text-lg text-primary">Digital</h3>
                    <a href="mailto:Sooners.P.M.Co@Gmail.com" className="text-text-muted block hover:underline">Sooners.P.M.Co@Gmail.com</a>
                    <span className="text-text-muted">www.soonersland.com</span>
                  </div>
                </div>

                <div className="flex gap-4">
                  <MapPin size={24} className="text-accent shrink-0 mt-1" />
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-primary mb-1">Oklahoma City Headquarters 🇺🇸</h4>
                      <p className="text-text-muted text-sm leading-relaxed">
                        2025 South Nicklas Ave., Suite 115<br />Oklahoma City, OK 73128
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-primary p-8 rounded-3xl text-white shadow-xl">
              <h4 className="font-bold mb-2">Technical Assistance</h4>
              <p className="text-slate-400 text-sm">For technical inquiries based in Turkey or the Middle East, please call our Istanbul office directly.</p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3"
          >
            <div className="bg-white p-10 md:p-14 rounded-[2.5rem] shadow-2xl border border-slate-100">
              <h2 className="text-3xl font-extrabold mb-4 text-primary">Send a Message</h2>
              <p className="text-text-muted mb-10 text-lg">Tell us about your project or inquiry, and we'll get back to you shortly.</p>

              {isSent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-emerald-50 text-emerald-700 p-4 rounded-xl mb-8 flex items-center gap-3 border border-emerald-100"
                >
                  <CheckCircle2 size={24} />
                  <span className="font-semibold">Message sent successfully! We'll be in touch soon.</span>
                </motion.div>
              )}

              {error && (
                <div className="bg-rose-50 text-rose-700 p-4 rounded-xl mb-8 flex items-center gap-3 border border-rose-100">
                  <AlertCircle size={24} />
                  <span className="font-semibold">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-primary">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-5 py-4 rounded-2xl bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/10 transition-all outline-none"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-primary">Work Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="w-full px-5 py-4 rounded-2xl bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/10 transition-all outline-none"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-primary">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-5 py-4 rounded-2xl bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/10 transition-all outline-none"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-primary">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help with your project?"
                    className="w-full px-5 py-4 rounded-2xl bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/10 transition-all outline-none min-h-[160px]"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className={`btn-primary w-full py-5 text-lg font-bold shadow-xl shadow-accent/20 ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">Sending...</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Submit Inquiry <Send size={20} />
                    </span>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;